import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShoppingBag,
  TrendingUp,
  Wallet,
  Users,
  Clock,
  AlertCircle,
  Package,
  Truck,
  Home,
  ChevronRight,
  IndianRupee,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_COLOR = {
  pending: '#f59e0b',
  confirmed: '#3b82f6',
  preparing: '#6366f1',
  ready: '#a855f7',
  out_for_delivery: '#06b6d4',
  delivered: '#10b981',
  cancelled: '#ef4444',
};

const STATUS_BADGE_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  preparing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  ready: 'bg-purple-50 text-purple-700 border-purple-200',
  out_for_delivery: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const formatINR = (v) =>
  '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const formatDay = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
const formatTimeAgo = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
};

const VendorDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/vendor/dashboard`, {
          withCredentials: true,
        });
        setData(res.data);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  const k = data.kpis || {};
  const statusEntries = Object.entries(data.status_breakdown || {})
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value, color: STATUS_COLOR[name] }));
  const totalOrdersForPie = statusEntries.reduce((s, e) => s + e.value, 0);
  const deliveryEntries = [
    {
      name: 'Pickup',
      value: data.delivery_split?.pickup || 0,
      color: '#0ea5e9',
      Icon: Truck,
    },
    {
      name: 'Home delivery',
      value: data.delivery_split?.home_delivery || 0,
      color: '#22c55e',
      Icon: Home,
    },
  ];

  return (
    <div data-testid="vendor-dashboard-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">
          Dashboard
        </h1>
        <p className="font-body text-text-secondary">
          Overview of your store performance
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-5">
        <Kpi
          label="Total revenue"
          value={formatINR(k.total_revenue)}
          sub={`${k.delivered_orders} delivered`}
          icon={IndianRupee}
          highlight
        />
        <Kpi
          label="Today's revenue"
          value={formatINR(k.today_revenue)}
          sub={`${k.today_orders} order${k.today_orders === 1 ? '' : 's'} today`}
          icon={TrendingUp}
        />
        <Kpi
          label="Active orders"
          value={k.active_orders}
          sub="In progress"
          icon={Clock}
        />
        <Kpi
          label="Total orders"
          value={k.total_orders}
          sub={`${k.cancelled_orders} cancelled`}
          icon={ShoppingBag}
        />
        <Kpi
          label="Customers"
          value={k.unique_customers}
          sub="Unique"
          icon={Users}
        />
        <Kpi
          label="Avg order value"
          value={formatINR(k.avg_order_value)}
          sub="Across delivered"
          icon={Package}
        />
        <Kpi
          label="Unpaid (delivered)"
          value={formatINR(k.unpaid_amount)}
          sub="Awaiting collection"
          icon={Wallet}
          warn={Number(k.unpaid_amount) > 0}
        />
        <Kpi
          label="Cancellation rate"
          value={
            k.total_orders > 0
              ? `${Math.round((k.cancelled_orders / k.total_orders) * 100)}%`
              : '0%'
          }
          sub={`${k.cancelled_orders}/${k.total_orders}`}
          icon={AlertCircle}
        />
      </div>

      {/* Revenue & order trend (30-day) */}
      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-2xl border border-surface-border p-4 lg:col-span-2">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <h2 className="font-heading font-bold text-lg text-text-primary">
                Revenue (last 30 days)
              </h2>
              <p className="font-body text-xs text-text-muted">
                Delivered orders only
              </p>
            </div>
            <div className="font-heading text-xl font-bold text-text-primary">
              {formatINR(
                (data.timeseries || []).reduce((s, d) => s + d.revenue, 0)
              )}
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data.timeseries}
                margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickFormatter={formatDay}
                  interval="preserveStartEnd"
                  minTickGap={28}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  width={50}
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                  }}
                  labelFormatter={formatDay}
                  formatter={(v) => [formatINR(v), 'Revenue']}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#6366f1"
                  strokeWidth={2}
                  fill="url(#revFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-surface-border p-4">
          <h2 className="font-heading font-bold text-lg text-text-primary">
            Daily orders
          </h2>
          <p className="font-body text-xs text-text-muted mb-3">
            All orders in the last 30 days
          </p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.timeseries}
                margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickFormatter={formatDay}
                  interval="preserveStartEnd"
                  minTickGap={28}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  allowDecimals={false}
                  width={32}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    fontSize: 12,
                  }}
                  labelFormatter={formatDay}
                  formatter={(v) => [v, 'Orders']}
                />
                <Bar dataKey="orders" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Order status + delivery split + top products */}
      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        {/* Status pie */}
        <div className="bg-white rounded-2xl border border-surface-border p-4">
          <h2 className="font-heading font-bold text-lg text-text-primary mb-1">
            Order status mix
          </h2>
          <p className="font-body text-xs text-text-muted mb-3">All-time</p>
          {totalOrdersForPie === 0 ? (
            <Empty>No orders yet</Empty>
          ) : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusEntries}
                      dataKey="value"
                      innerRadius={42}
                      outerRadius={70}
                      paddingAngle={2}
                    >
                      {statusEntries.map((e) => (
                        <Cell key={e.name} fill={e.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid #e2e8f0',
                        fontSize: 12,
                      }}
                      formatter={(v, n) => [v, STATUS_LABELS[n] || n]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
                {statusEntries.map((e) => (
                  <div
                    key={e.name}
                    className="flex items-center gap-1.5 text-xs font-body"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ background: e.color }}
                    ></span>
                    <span className="text-text-secondary">
                      {STATUS_LABELS[e.name]}
                    </span>
                    <span className="ml-auto font-medium text-text-primary">
                      {e.value}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Delivery split */}
        <div className="bg-white rounded-2xl border border-surface-border p-4">
          <h2 className="font-heading font-bold text-lg text-text-primary mb-1">
            Delivery split
          </h2>
          <p className="font-body text-xs text-text-muted mb-3">
            Pickup vs home delivery (line items)
          </p>
          {deliveryEntries.every((e) => e.value === 0) ? (
            <Empty>No items yet</Empty>
          ) : (
            <div className="space-y-3 mt-1">
              {deliveryEntries.map(({ name, value, color, Icon }) => {
                const total =
                  deliveryEntries[0].value + deliveryEntries[1].value;
                const pct = total > 0 ? (value / total) * 100 : 0;
                return (
                  <div key={name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-sm font-body text-text-primary">
                        <Icon size={14} style={{ color }} />
                        {name}
                      </span>
                      <span className="font-heading font-bold text-sm text-text-primary">
                        {value}{' '}
                        <span className="text-text-muted text-xs font-body">
                          ({Math.round(pct)}%)
                        </span>
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-background-secondary overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top products */}
        <div className="bg-white rounded-2xl border border-surface-border p-4">
          <h2 className="font-heading font-bold text-lg text-text-primary mb-1">
            Top products
          </h2>
          <p className="font-body text-xs text-text-muted mb-3">
            By delivered revenue
          </p>
          {(data.top_products || []).length === 0 ? (
            <Empty>No sales yet</Empty>
          ) : (
            <div className="space-y-2.5">
              {data.top_products.map((p, idx) => (
                <div key={p.product_id} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-md bg-brand-primary/10 text-brand-primary font-heading font-bold text-xs flex items-center justify-center shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-text-primary truncate">
                      {p.name}
                    </p>
                    <p className="font-body text-xs text-text-muted">
                      {p.delivered_qty} delivered · {p.qty_sold} ordered
                    </p>
                  </div>
                  <p className="font-heading font-bold text-sm text-text-primary shrink-0">
                    {formatINR(p.revenue)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent orders */}
      <div className="bg-white rounded-2xl border border-surface-border p-4">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-heading font-bold text-lg text-text-primary">
            Recent orders
          </h2>
          <button
            type="button"
            onClick={() => navigate('/vendor/orders')}
            className="text-sm font-body text-brand-primary hover:underline inline-flex items-center gap-1"
            data-testid="dashboard-view-all-orders"
          >
            View all <ChevronRight size={14} />
          </button>
        </div>
        {(data.recent_orders || []).length === 0 ? (
          <Empty>No orders yet</Empty>
        ) : (
          <div className="divide-y divide-surface-border">
            {data.recent_orders.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => navigate(`/vendor/orders/${o.id}`)}
                className="w-full text-left flex items-center gap-3 py-3 group"
                data-testid={`dashboard-recent-${o.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold text-text-primary text-sm">
                      {o.order_number}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                        STATUS_BADGE_STYLES[o.status] || ''
                      }`}
                    >
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>
                  <p className="font-body text-xs text-text-muted mt-0.5 truncate">
                    {o.customer_name || 'Customer'} · {formatTimeAgo(o.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-heading font-bold text-sm text-text-primary">
                    {formatINR(o.total_amount)}
                  </p>
                  <p className="font-body text-[11px] text-text-muted">
                    {o.item_count} item{o.item_count === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight
                  size={16}
                  className="text-text-muted group-hover:text-brand-primary transition-colors"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, icon: Icon, highlight, warn }) => (
  <div
    className={`rounded-2xl border p-4 ${
      highlight
        ? 'bg-brand-primary/5 border-brand-primary/20'
        : warn
        ? 'bg-amber-50 border-amber-200'
        : 'bg-white border-surface-border'
    }`}
    data-testid={`dashboard-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-body text-[11px] uppercase tracking-wide text-text-muted">
        {label}
      </span>
      {Icon && (
        <Icon
          size={15}
          className={highlight ? 'text-brand-primary' : 'text-text-muted'}
        />
      )}
    </div>
    <p
      className={`font-heading font-bold text-2xl ${
        highlight
          ? 'text-brand-primary'
          : warn
          ? 'text-amber-700'
          : 'text-text-primary'
      }`}
    >
      {value}
    </p>
    {sub && (
      <p className="font-body text-[11px] text-text-muted mt-0.5">{sub}</p>
    )}
  </div>
);

const Empty = ({ children }) => (
  <div className="h-32 flex items-center justify-center text-text-muted font-body text-sm">
    {children}
  </div>
);

export default VendorDashboard;
